/**
 * TRANSITIONAL IMPLEMENTATION
 *
 * This file serves as a bridge between the legacy score calculations and the new implementation
 * in newScoreCalculations.ts. It maintains backward compatibility while allowing gradual
 * migration to the new calculation methods.
 *
 * Current behavior:
 * 1. Attempts to use new calculations first (from newScoreCalculations.ts)
 * 2. Falls back to legacy calculations if new method fails
 * 3. Maintains consistent interface for other components
 *
 * TODO: Migration to New Calculations
 * When ready to fully migrate to the new calculations:
 * 1. Verify all new calculations are working correctly with production data
 * 2. Confirm all required fields are consistently available in the database
 * 3. Test thoroughly across all member scenarios
 * 4. Communicate changes to stakeholders
 * 5. Remove legacy calculation code
 * 6. Consider renaming newScoreCalculations.ts to scoreCalculations.ts
 * 7. Delete this file
 *
 * Migration Command for Future Reference:
 * ```bash
 * # 1. Rename new implementation to replace old
 * mv src/utils/newScoreCalculations.ts src/utils/scoreCalculations.ts
 *
 * # 2. Update imports in all files from:
 * # import { calculateScoreMetrics } from '../utils/scoreCalculations';
 * # to use the specific functions from the new implementation:
 * # import { calculateHistoricalScoresFromRawData } from '../utils/scoreCalculations';
 * ```
 */

/**
 * Utility functions for score calculations and formatting
 */

import { ScoreHistory, getHistoricalScores } from '../api/scoreData';
import type { FinancialHealthStats } from '../types/financialStats';
import { ScoreMetrics } from '../types/scoreMetrics';
import { calculateHistoricalScoresFromRawData } from './newScoreCalculations';
import { VeraScoreInputs } from '../types/veraScoreInputs';

/**
 * Rounds a score to the nearest whole number
 */
export const roundScore = (score: number): number => {
  return Math.round(score);
};

/**
 * Calculates the average score over a period
 */
export const calculateAverageScore = (scores: ScoreHistory[]): number => {
  if (!scores.length) return 0;
  const sum = scores.reduce((acc, score) => acc + (score.verascore ?? 0), 0);
  return roundScore(sum / scores.length);
};

/**
 * Determines score trend direction
 */
export const calculateTrendDirection = (scores: ScoreHistory[]): 'up' | 'down' | 'stable' => {
  if (scores.length < 2) return 'stable';

  const recent = scores[0]?.verascore ?? 0;
  const previous = scores[1]?.verascore ?? 0;
  const difference = recent - previous;

  if (Math.abs(difference) < 5) return 'stable';
  return difference > 0 ? 'up' : 'down';
};

/**
 * Determines score health status
 */
export const getScoreHealth = (score: number): 'good' | 'fair' | 'poor' => {
  if (score >= 80) return 'good';
  if (score >= 60) return 'fair';
  return 'poor';
};

/**
 * Calculates all score metrics
 */
export const calculateScoreMetrics = (scores: ScoreHistory[]): ScoreMetrics => {
  if (!scores.length) {
    return {
      current_score: 0,
      six_month_avg: null,
      twelve_month_avg: null,
      twenty_four_month_avg: null,
      trend_direction: 'stable',
      health_status: 'poor',
    };
  }

  // Current score is just the verascore from most recent month
  const current_score = scores[0].verascore ?? 0;

  // Try to use the new calculation method first
  try {
    // Map the scores to the format expected by the new calculation
    const monthlyData = scores.map(score => ({
      month: score.month,
      grossIncome: score.gross_income ?? null,
      minDebtPayments: score.minimum_debt_payments ?? null,
      liquidReserves: score.liquid_reserves ?? null,
      monthlyExpenses: score.monthly_expenses ?? null,
      moneyIn: score.money_in ?? null,
      netSavings: score.net_savings ?? null,
      outstandingDebt: score.total_outstanding_adverse_debt ?? null,
      debtPayments: score.actual_adverse_debt_payments ?? null,
      latePayments: score.number_of_late_payments ?? null,
    }));

    // Check if we have any valid data
    const hasValidData = monthlyData.some(data =>
      Object.values(data).some(val => val !== null && val !== '')
    );

    if (!hasValidData) {
      return {
        current_score,
        six_month_avg: null,
        twelve_month_avg: null,
        twenty_four_month_avg: null,
        trend_direction: calculateTrendDirection(scores),
        health_status: getScoreHealth(current_score),
      };
    }

    const newCalculation = calculateHistoricalScoresFromRawData(monthlyData);

    return {
      current_score,
      six_month_avg: newCalculation.six_month_avg || null,
      twelve_month_avg: newCalculation.twelve_month_avg || null,
      twenty_four_month_avg: newCalculation.twenty_four_month_avg || null,
      trend_direction: calculateTrendDirection(scores),
      health_status: getScoreHealth(current_score),
    };
  } catch (error) {
    console.error('Error in new calculation, falling back to original method', error);
  }

  // Use the original calculation method for backward compatibility
  const six_month_scores = scores.slice(0, 6);
  const twelve_month_scores = scores.slice(0, 12);
  const twenty_four_month_scores = scores.slice(0, 24);

  // Check if we have any valid factor scores
  const hasValidFactors = scores.some(
    score =>
      score.factor_dti !== null ||
      score.factor_cash_on_hand !== null ||
      score.factor_spending !== null ||
      score.factor_savings !== null ||
      score.factor_debt !== null ||
      score.factor_payment_history !== null
  );

  if (!hasValidFactors) {
    return {
      current_score,
      six_month_avg: null,
      twelve_month_avg: null,
      twenty_four_month_avg: null,
      trend_direction: calculateTrendDirection(scores),
      health_status: getScoreHealth(current_score),
    };
  }

  const six_month_avg = roundScore(
    six_month_scores.reduce((sum, score) => {
      return (
        sum +
        ((score.factor_dti ?? 0) * 0.2 +
          (score.factor_cash_on_hand ?? 0) * 0.25 +
          (score.factor_spending ?? 0) * 0.2 +
          (score.factor_savings ?? 0) * 0.15 +
          (score.factor_debt ?? 0) * 0.05 +
          (score.factor_payment_history ?? 0) * 0.15)
      );
    }, 0) / (six_month_scores.length || 1)
  );

  const twelve_month_avg = roundScore(
    twelve_month_scores.reduce((sum, score) => {
      return (
        sum +
        ((score.factor_dti ?? 0) * 0.2 +
          (score.factor_cash_on_hand ?? 0) * 0.25 +
          (score.factor_spending ?? 0) * 0.2 +
          (score.factor_savings ?? 0) * 0.15 +
          (score.factor_debt ?? 0) * 0.05 +
          (score.factor_payment_history ?? 0) * 0.15)
      );
    }, 0) / (twelve_month_scores.length || 1)
  );

  const twenty_four_month_avg = roundScore(
    twenty_four_month_scores.reduce((sum, score) => {
      return (
        sum +
        ((score.factor_dti ?? 0) * 0.2 +
          (score.factor_cash_on_hand ?? 0) * 0.25 +
          (score.factor_spending ?? 0) * 0.2 +
          (score.factor_savings ?? 0) * 0.15 +
          (score.factor_debt ?? 0) * 0.05 +
          (score.factor_payment_history ?? 0) * 0.15)
      );
    }, 0) / (twenty_four_month_scores.length || 1)
  );

  // If all factors were 0, return null instead
  const allFactorsZero = six_month_scores.every(
    score =>
      (score.factor_dti ?? 0) === 0 &&
      (score.factor_cash_on_hand ?? 0) === 0 &&
      (score.factor_spending ?? 0) === 0 &&
      (score.factor_savings ?? 0) === 0 &&
      (score.factor_debt ?? 0) === 0 &&
      (score.factor_payment_history ?? 0) === 0
  );

  return {
    current_score,
    six_month_avg: allFactorsZero ? null : six_month_avg,
    twelve_month_avg: allFactorsZero ? null : twelve_month_avg,
    twenty_four_month_avg: allFactorsZero ? null : twenty_four_month_avg,
    trend_direction: calculateTrendDirection(scores),
    health_status: getScoreHealth(current_score),
  };
};

/**
 * Calculates trend based on historical data
 */
export const calculateScoreTrend = (_scores: ScoreHistory[]): number => {
  // Pure calculation logic here
  return roundScore(0); // Replace 0 with actual calculation
};

/**
 * Higher-level function that fetches data and calculates trend
 */
export const getScoreTrendWithData = async (uuid: string, months: number): Promise<number> => {
  const historicalData = await getHistoricalScores(uuid, months);
  return calculateScoreTrend(historicalData);
};

/**
 * Calculates the historical score trend (6 months)
 */
export const calculateSixMonthTrend = (_historicalScores: number[]): number => {
  // Implementation here
  return roundScore(0); // Replace 0 with actual calculation
};

/**
 * Calculates the historical score trend (12 months)
 */
export const calculateTwelveMonthTrend = (_historicalScores: number[]): number => {
  // Implementation here
  return roundScore(0); // Replace 0 with actual calculation
};

interface WeightedFactors {
  factor_dti: number;
  factor_cash_on_hand: number;
  factor_spending: number;
  factor_savings: number;
  factor_debt: number;
  factor_payment_history: number;
}

const FACTOR_WEIGHTS: WeightedFactors = {
  factor_dti: 0.2,
  factor_cash_on_hand: 0.25,
  factor_spending: 0.2,
  factor_savings: 0.15,
  factor_debt: 0.05,
  factor_payment_history: 0.15,
};

/**
 * Original weighted score calculation for backward compatibility
 */
export const calculateWeightedScore = (stats: FinancialHealthStats[]): number => {
  if (!stats.length) return 0;

  return (
    stats.reduce((sum, stat) => {
      const weightedSum =
        (stat.factor_dti ?? 0) * FACTOR_WEIGHTS.factor_dti +
        (stat.factor_cash_on_hand ?? 0) * FACTOR_WEIGHTS.factor_cash_on_hand +
        (stat.factor_spending ?? 0) * FACTOR_WEIGHTS.factor_spending +
        (stat.factor_savings ?? 0) * FACTOR_WEIGHTS.factor_savings +
        (stat.factor_debt ?? 0) * FACTOR_WEIGHTS.factor_debt +
        (stat.factor_payment_history ?? 0) * FACTOR_WEIGHTS.factor_payment_history;

      return sum + weightedSum;
    }, 0) / stats.length
  );
};

export const calculateHistoricalScores = async (
  stats: FinancialHealthStats[]
): Promise<{ six_month_avg: number; twelve_month_avg: number }> => {
  // Sort by month descending to get most recent first
  const sortedStats = [...stats].sort(
    (a, b) => new Date(b.month).getTime() - new Date(a.month).getTime()
  );

  const sixMonthStats = sortedStats.slice(0, 6);
  const twelveMonthStats = sortedStats.slice(0, 12);

  return {
    six_month_avg: Math.round(calculateWeightedScore(sixMonthStats)),
    twelve_month_avg: Math.round(calculateWeightedScore(twelveMonthStats)),
  };
};

/**
 * Calculates ScoreMetrics from FinancialHealthStats array.
 * This allows MemberCard to compute metrics from the financialData prop
 * without making an additional API call through useScoreData.
 */
export const calculateScoreMetricsFromStats = (stats: FinancialHealthStats[]): ScoreMetrics => {
  if (!stats || stats.length === 0) {
    return {
      current_score: 0,
      six_month_avg: null,
      twelve_month_avg: null,
      twenty_four_month_avg: null,
      trend_direction: 'stable',
      health_status: 'poor',
    };
  }

  // Sort by month descending to get most recent first
  const sortedStats = [...stats].sort(
    (a, b) => new Date(b.month).getTime() - new Date(a.month).getTime()
  );

  // Current score is the verascore from the most recent month
  const current_score = sortedStats[0]?.verascore ?? 0;

  // Calculate averages for different time periods
  const sixMonthStats = sortedStats.slice(0, 6);
  const twelveMonthStats = sortedStats.slice(0, 12);
  const twentyFourMonthStats = sortedStats.slice(0, 24);

  // Check if we have any valid factor scores
  const hasValidFactors = sortedStats.some(
    stat =>
      stat.factor_dti !== null ||
      stat.factor_cash_on_hand !== null ||
      stat.factor_spending !== null ||
      stat.factor_savings !== null ||
      stat.factor_debt !== null ||
      stat.factor_payment_history !== null
  );

  if (!hasValidFactors) {
    return {
      current_score,
      six_month_avg: null,
      twelve_month_avg: null,
      twenty_four_month_avg: null,
      trend_direction: calculateTrendDirectionFromStats(sortedStats),
      health_status: getScoreHealth(current_score),
    };
  }

  const six_month_avg = Math.round(calculateWeightedScore(sixMonthStats));
  const twelve_month_avg = Math.round(calculateWeightedScore(twelveMonthStats));
  const twenty_four_month_avg = Math.round(calculateWeightedScore(twentyFourMonthStats));

  // Check if all factors were 0
  const allFactorsZero = sixMonthStats.every(
    stat =>
      (stat.factor_dti ?? 0) === 0 &&
      (stat.factor_cash_on_hand ?? 0) === 0 &&
      (stat.factor_spending ?? 0) === 0 &&
      (stat.factor_savings ?? 0) === 0 &&
      (stat.factor_debt ?? 0) === 0 &&
      (stat.factor_payment_history ?? 0) === 0
  );

  return {
    current_score,
    six_month_avg: allFactorsZero ? null : six_month_avg,
    twelve_month_avg: allFactorsZero ? null : twelve_month_avg,
    twenty_four_month_avg: allFactorsZero ? null : twenty_four_month_avg,
    trend_direction: calculateTrendDirectionFromStats(sortedStats),
    health_status: getScoreHealth(current_score),
  };
};

/**
 * Helper to calculate trend direction from FinancialHealthStats
 */
const calculateTrendDirectionFromStats = (stats: FinancialHealthStats[]): 'up' | 'down' | 'stable' => {
  if (stats.length < 2) return 'stable';

  const recent = stats[0]?.verascore ?? 0;
  const previous = stats[1]?.verascore ?? 0;
  const difference = recent - previous;

  if (Math.abs(difference) < 5) return 'stable';
  return difference > 0 ? 'up' : 'down';
};

/**
 * Adapter function to convert existing data format to new VeraScore inputs
 */
export const adaptToVeraScoreInputs = (data: {
  gross_income?: number | null;
  minimum_debt_payments?: number | null;
  liquid_reserves?: number | null;
  monthly_expenses?: number | null;
  money_in?: number | null;
  net_savings?: number | null;
  total_outstanding_adverse_debt?: number | null;
  actual_adverse_debt_payments?: number | null;
  number_of_late_payments?: number | null;
  age_most_recent_late?: number | null;
}): VeraScoreInputs => {
  return {
    grossIncome: data.gross_income ?? 0,
    minimumDebtPayments: data.minimum_debt_payments ?? 0,
    liquidReserves: data.liquid_reserves ?? 0,
    monthlyExpenses: data.monthly_expenses ?? 0,
    moneyLeftover: (data.money_in ?? 0) - (data.monthly_expenses ?? 0),
    netSavings: data.net_savings ?? 0,
    totalOutstandingAdverseDebt: data.total_outstanding_adverse_debt ?? 0,
    actualAdverseDebtPayments: data.actual_adverse_debt_payments ?? 0,
    ageOfMostRecentLatePayment: data.age_most_recent_late ?? 0,
    numberOfLatePayments: data.number_of_late_payments ?? 0
  };
};

// Add more calculation functions as needed
